import React, { useEffect, useState, useRef, memo } from "react";
import * as pdfjsLib from "pdfjs-dist";
import styles from "./PdfThumbnail.module.scss";

// Set up the PDF.js worker
const WORKER_URL = "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js";
pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL;

// Create a global cache for thumbnails to prevent reloading
const thumbnailCache = new Map();

// Pre-load the worker script to prevent repeated requests
const preloadWorker = () => {
  if (!('workerPreloaded' in window)) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = WORKER_URL;
    document.head.appendChild(link);
    window.workerPreloaded = true;
  }
};

// Call preload function immediately
preloadWorker();

const PdfThumbnail = ({ fileUrl, publishDate, pageNumber, coverUrl, onError }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [formattedDate, setFormattedDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false); // Track errors
  
  const cacheKey = `${fileUrl}-${pageNumber || 1}`;
  const isMountedRef = useRef(true);
  const loadAttemptedRef = useRef(false);
  const prevFileUrlRef = useRef(fileUrl);
  
  useEffect(() => {
    // Reset loadAttemptedRef and thumbnailUrl if fileUrl has changed
    if (prevFileUrlRef.current !== fileUrl) {
      loadAttemptedRef.current = false;
      setThumbnailUrl(null); 
      setIsLoading(false); 
      setHasError(false); // Reset error state on URL change
      prevFileUrlRef.current = fileUrl;
    }

    if (coverUrl) {
      return;
    }
    
    // Strict check: if fileUrl is null/undefined, treat it as an error/empty state immediately
    if (!fileUrl) {
        setHasError(true);
        if (onError) onError(); // Notify parent
        return;
    }
    
    // Prevent redundant fetches
    if (isLoading || thumbnailUrl || loadAttemptedRef.current || hasError) {
        return;
    }
    
    if (thumbnailCache.has(cacheKey)) {
      setThumbnailUrl(thumbnailCache.get(cacheKey));
      return;
    }

    setIsLoading(true);
    loadAttemptedRef.current = true;
    
    const fetchThumbnail = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(fileUrl);
        const pdf = await loadingTask.promise;

        if (!isMountedRef.current) return;

        const pageToRender = await pdf.getPage(1);
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const viewport = pageToRender.getViewport({ scale: 0.9 });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        await pageToRender.render(renderContext).promise;

        const dataUrl = canvas.toDataURL();
        thumbnailCache.set(cacheKey, dataUrl);
        if (isMountedRef.current) {
            setThumbnailUrl(dataUrl);
        }
      } catch (error) {
        // Specifically catch MissingPDFException (404s) and general errors
        if (error.name === 'MissingPDFException' || (error.message && error.message.includes('404')) || !fileUrl) {
             console.warn(`[PdfThumbnail] Hiding thumbnail due to missing/invalid PDF: ${fileUrl}`);
             if (isMountedRef.current) {
                 setHasError(true);
                 if (onError) onError(); // Notify parent to hide card
             }
        } else {
             console.error(`[PdfThumbnail]: Error fetching PDF thumbnail for fileUrl: ${fileUrl}.`, error);
             if (isMountedRef.current) {
                 setHasError(true);
                 if (onError) onError(); // Notify parent to hide card
             }
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchThumbnail();
    
    return () => {
      isMountedRef.current = false;
    };
    
  }, [cacheKey, coverUrl, fileUrl, isLoading, pageNumber, thumbnailUrl, hasError]);

  useEffect(() => {
    if (!publishDate) return;
    
    const formatDate = (dateString) => {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);
      return isNaN(date.getTime())
        ? "Invalid date"
        : date.toLocaleDateString(undefined, options);
    };

    setFormattedDate(formatDate(publishDate));
  }, [publishDate]);

  // If there's an error or no URL (and no cover), hide the component entirely (return null)
  // coverUrl takes precedence, so we show it if it exists even if PDF fetch failed
  if (hasError && !coverUrl) {
      return null;
  }
  
  // Also default to null if no fileUrl is provided at all and no cover
  if (!fileUrl && !coverUrl) return null;

  return (
    <div className={styles.thumbnailContainer}>
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={`PDF Cover - Page ${pageNumber != null ? pageNumber : 1}`}
          className={styles.thumbnailImage}
          loading="lazy"
        />
      ) : thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={`PDF Thumbnail - Page ${pageNumber != null ? pageNumber : 1}`}
          className={styles.thumbnailImage}
          loading="lazy"
        />
      ) : (
        <div className={styles.loader}></div>
      )}
    </div>
  );
};

// Use React.memo with a custom comparison function to prevent unnecessary re-renders
export default memo(PdfThumbnail, (prevProps, nextProps) => {
  // Only re-render if the fileUrl or pageNumber has changed
  return (
    prevProps.fileUrl === nextProps.fileUrl &&
    prevProps.pageNumber === nextProps.pageNumber &&
    prevProps.coverUrl === nextProps.coverUrl
  );
});

// import React, { useEffect, useState } from "react";
// import { Worker, Viewer } from "@react-pdf-viewer/core";
// import "@react-pdf-viewer/core/lib/styles/index.css";
// import { pdfjs } from "pdfjs-dist";
// import styles from "./PdfThumbnail.module.scss";

// const PdfThumbnail = ({ fileUrl }) => {
//     const [thumbnailUrl, setThumbnailUrl] = useState(null);

//     useEffect(() => {
//       const fetchThumbnail = async () => {
//         try {
//           const pdf = await pdfjs.getDocument(fileUrl).promise;
//           const page = await pdf.getPage(1);

//           const canvas = document.createElement('canvas');
//           const context = canvas.getContext('2d');
//           const viewport = page.getViewport({ scale: 0.2 });
//           canvas.height = viewport.height;
//           canvas.width = viewport.width;

//           const renderContext = {
//             canvasContext: context,
//             viewport: viewport,
//           };
//           await page.render(renderContext).promise;
//           setThumbnailUrl(canvas.toDataURL());
//         } catch (error) {
//           console.error("Error fetching PDF thumbnail", error);
//         }
//       };

//       fetchThumbnail();
//     }, [fileUrl]);

//   return (
//     <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
//       <Viewer
//         fileUrl={fileUrl}
//         renderThumbnail={() => (
//           <img src={thumbnailUrl} alt="PDF Thumbnail" className={styles.thumbnailImage} />
//         )}
//       />
//     </Worker>
//   );

// //   return thumbnailUrl ? (
// //     <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
// //       <Viewer
// //         fileUrl={fileUrl}
// //         renderThumbnail={() => (
// //           <img
// //             src={thumbnailUrl}
// //             alt="PDF Thumbnail"
// //             className={styles.thumbnailImage}
// //           />
// //         )}
// //       />
// //     </Worker>
// //   ) : (
// //     <div>Loading...</div>
// //   );
// };

// export default PdfThumbnail;
